import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AnalyticsService } from '../../../services/analytics.service';
import { OrcamentoVendasMesApi } from '../../../models/analytics.model';
import { todayLocalDateString } from '../../../core/utils/date.util';

interface DashboardMetric {
  title: string;
  currentValue: number;
  previousValue: number;
  trend: number;
  trendPositive: boolean;
  periodLabel: string;
  previousPeriodLabel: string;
  sparklineValues: number[];
  isLoading: boolean;
}

interface Periodos {
  inicioAtual: string;
  fimAtual: string;
  inicioAnterior: string;
  fimAnterior: string;
  mesAtualLabel: string;    // formato MM/YYYY — usado para localizar o mês no array da API
  mesAnteriorLabel: string;
}

@Component({
  selector: 'app-dashboard-grid',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dashboard-grid.component.html',
  styleUrl: './dashboard-grid.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardGridComponent implements OnInit {
  leadsIndicador      = this.emptyMetric('Leads do mês');
  orcamentosIndicador = this.emptyMetric('Orçamentos do mês');
  vendasIndicador     = this.emptyMetric('Vendas do mês');

  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private analyticsService: AnalyticsService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.leadsIndicador.isLoading      = true;
    this.orcamentosIndicador.isLoading = true;
    this.vendasIndicador.isLoading     = true;
    this.cdr.detectChanges();
    const p = this.periodos();


    // A API agrega por mês (mesAno); uma única chamada cobrindo os dois meses
    // traria o mês anterior inteiro. Por isso, uma chamada por período:
    // mês atual (dia 1 → hoje) e mês anterior (dia 1 → mesmo dia de hoje).
    forkJoin({
      atual:    this.analyticsService.getVendasOrcamentosPorMes(p.inicioAtual, p.fimAtual),
      anterior: this.analyticsService.getVendasOrcamentosPorMes(p.inicioAnterior, p.fimAnterior)
    }).subscribe({
      next: ({ atual, anterior }) => {
        const dadosAtual    = atual.find(d => d.mesAno === p.mesAtualLabel)        ?? this.emptyApiData();
        const dadosAnterior = anterior.find(d => d.mesAno === p.mesAnteriorLabel) ?? this.emptyApiData();

        this.leadsIndicador      = this.buildMetric('Leads do mês',      dadosAtual.quantidadeLeads,      dadosAnterior.quantidadeLeads,      p);
        this.orcamentosIndicador = this.buildMetric('Orçamentos do mês', dadosAtual.quantidadeOrcamentos, dadosAnterior.quantidadeOrcamentos, p);
        this.vendasIndicador     = this.buildMetric('Vendas do mês',     dadosAtual.quantidadeVendas,     dadosAnterior.quantidadeVendas,     p);
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error('Não foi possível carregar os indicadores do dashboard.');
        this.leadsIndicador.isLoading      = false;
        this.orcamentosIndicador.isLoading = false;
        this.vendasIndicador.isLoading     = false;
        this.cdr.detectChanges();
      }
    });
  }

  sparklinePath(values: number[], index: number): string {
    if (!values.length) return '';

    const width   = 220;
    const height  = 72;
    const padding = 10;
    const allMin  = Math.min(...values);
    const allMax  = Math.max(...values);
    const range   = allMax - allMin || 1;

    const totalPoints = 2;
    const step = (width - 2 * padding) / (totalPoints - 1);

    const x = padding + index * step;
    const y = height - padding - ((values[index] - allMin) / range) * (height - 2 * padding);
    return `${x},${y}`;
  }

  sparklineLinePath(values: number[]): string {
    if (values.length < 2) return '';

    const width   = 220;
    const height  = 72;
    const padding = 10;
    const allMin  = Math.min(...values);
    const allMax  = Math.max(...values);
    const range   = allMax - allMin || 1;
    const step    = (width - 2 * padding) / (values.length - 1);

    return values
      .map((v, i) => {
        const x = padding + i * step;
        const y = height - padding - ((v - allMin) / range) * (height - 2 * padding);
        return `${x},${y}`;
      })
      .join(' ');
  }

  private periodos(): Periodos {
    // Usa todayLocalDateString() → meses já em base 1, sem conversão 0-based.
    const hoje = todayLocalDateString();             // 'yyyy-MM-dd'
    const [anoStr, mesStr, diaStr] = hoje.split('-');
    const ano = +anoStr;
    const mes = +mesStr;   // 1-based: junho = 6
    const dia = +diaStr;
    const pad = (n: number) => String(n).padStart(2, '0');

    const inicioAtual = `${ano}-${pad(mes)}-01`;
    const fimAtual    = `${ano}-${pad(mes)}-${pad(dia)}`;

    // Mês anterior (1-based)
    const anoPrev = mes === 1 ? ano - 1 : ano;
    const mesPrev = mes === 1 ? 12 : mes - 1;  // junho → 5 (maio)

    // Último dia do mês anterior:
    // new Date(year, month, 0) onde month é 0-based → mesPrev (1-based) já serve.
    // Ex.: mesPrev=5 → new Date(2026, 5, 0) = 31/mai ✓
    const ultimoDiaPrev = new Date(anoPrev, mesPrev, 0).getDate();
    const diaPrev = Math.min(dia, ultimoDiaPrev);

    const inicioAnterior = `${anoPrev}-${pad(mesPrev)}-01`;
    const fimAnterior    = `${anoPrev}-${pad(mesPrev)}-${pad(diaPrev)}`;

    // Labels no formato que a API retorna no campo mesAno (MM/YYYY)
    const mesAtualLabel    = `${pad(mes)}/${ano}`;
    const mesAnteriorLabel = `${pad(mesPrev)}/${anoPrev}`;

    return { inicioAtual, fimAtual, inicioAnterior, fimAnterior, mesAtualLabel, mesAnteriorLabel };
  }

  private buildMetric(title: string, current: number, previous: number, p: Periodos): DashboardMetric {
    const c    = Number.isFinite(current)  ? current  : 0;
    const prev = Number.isFinite(previous) ? previous : 0;
    const diff = prev > 0 ? ((c - prev) / prev) * 100 : (c > 0 ? 100 : 0);

    return {
      title,
      currentValue:        c,
      previousValue:       prev,
      trend:               Math.abs(diff),
      trendPositive:       diff >= 0,
      periodLabel:         `${p.inicioAtual} a ${p.fimAtual}`,
      previousPeriodLabel: `${p.inicioAnterior} a ${p.fimAnterior}`,
      sparklineValues:     [prev, c],
      isLoading:           false
    };
  }

  private emptyMetric(title: string): DashboardMetric {
    return {
      title,
      currentValue:        0,
      previousValue:       0,
      trend:               0,
      trendPositive:       true,
      periodLabel:         '—',
      previousPeriodLabel: '—',
      sparklineValues:     [0, 0],
      isLoading:           true
    };
  }

  private emptyApiData(): OrcamentoVendasMesApi {
    return {
      quantidadeOrcamentos: 0,
      mesAno:               '',
      totalVendas:          0,
      mes:                  '',
      totalOrcamentos:      0,
      quantidadeVendas:     0,
      taxaConversaoPct:     0,
      quantidadeLeads:      0
    };
  }
}
